import { AnalyticsContext, FederationContext } from '@/context';
import {
  CREATE_USER_GROUP_MUTATION,
  DELETE_USER_GROUP_MUTATION,
  UPDATE_USER_GROUP_MUTATION,
} from '@/mutations';
import { GET_FEDERATIONS, GET_FEDERATION_USERS } from '@/queries';
import { sortBy, uniqBy } from 'lodash';
import React, { useContext, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryResults } from '../../hooks';

import { GridView } from '@/components';
import { SettingsPanel } from '@/components/Settings/SettingsPanel';
import { useFormState } from '@hogwarts/ui-components-forms';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../hooks';

const UserGroups = () => {
  const federation = useContext(FederationContext);
  const { t } = useTranslation();
  const analytics = useContext(AnalyticsContext);

  const usersQuery = useQuery(GET_FEDERATION_USERS, {
    selector: 'federations[0].users',
    variables: {
      federationKey: federation.key,
    },
  });

  const userGroupsQuery = useQuery(GET_FEDERATIONS, {
    selector: 'federations[0].userGroups',
    variables: {
      federationKey: federation.key,
    },
  });

  const { loading, error } = useQueryResults({
    usersQuery,
    userGroupsQuery,
  });

  const [addGroup] = useMutation(CREATE_USER_GROUP_MUTATION, {
    variables: {
      federationKey: federation.key,
    },
    refetchQueries: [
      {
        query: GET_FEDERATIONS,
        variables: {
          federationKey: federation.key,
        },
      },
    ],
  });

  const [editGroup] = useMutation(UPDATE_USER_GROUP_MUTATION);

  const [deleteGroup] = useMutation(DELETE_USER_GROUP_MUTATION, {
    variables: {
      federationKey: federation.key,
    },
    refetchQueries: [
      {
        query: GET_FEDERATIONS,
        variables: {
          federationKey: federation.key,
        },
      },
    ],
  });

  const [ConfirmDeleteAlert, showConfirmDeleteAlert] = useAlert({
    icon: 'trash',
    intent: 'danger',
    confirmButtonText: t('Delete'),
    cancelButtonText: t('Cancel'),
    Content: () => (
      <p>
        {t(
          'Are you sure you want to delete this group? This operation cannot be undone.'
        )}
      </p>
    ),
    onConfirm: ({ id }) => {
      deleteGroup({ variables: { id } });
      analytics.events.groups.deleteUserGroupClicked({ id });
    },
  });

  const forms = useFormState();

  const users = usersQuery.data;
  const userGroups = userGroupsQuery.data;

  useEffect(() => {
    if (!users) return;

    // TODO: This needs to include the selected values as well
    // as we've had issues where there are users in the policy but
    // not in the list
    // and it looks like they arent selected.
    // This is why uniqBy exists.
    let userValues = sortBy(
      uniqBy(users.filter(Boolean), (u) => u.id),
      (u) => u.name
    ).map((user) => ({
      id: user.id,
      value: `${user.name} - ${user.email}`,
    }));

    forms.register('addGroup', {
      title: 'Add Group',
      fields: [
        {
          key: 'federationId',
          type: 'hidden',
        },
        {
          key: 'name',
          label: 'Name',
          validate: 'required',
        },
        {
          key: 'users',
          label: 'Users',
          type: 'multiselectlist',
          values: userValues,
        },
        {
          key: 'enabled',
          label: 'Enabled',
          type: 'toggle',
        },
      ],
      initialValues: {
        federationId: federation.id,
        enabled: true,
        users: [],
      },
      onSave: async (values) => {
        await addGroup({
          variables: {
            ...values,
          },
        });
      },
    });

    forms.register('editGroup', {
      title: 'Edit Group',
      fields: [
        {
          key: 'id',
          type: 'hidden',
        },
        {
          key: 'name',
          label: 'Name',
          validate: 'required',
        },
        {
          key: 'users',
          label: 'Users',
          type: 'multiselectlist',
          values: userValues,
        },
        {
          key: 'enabled',
          label: 'Enabled',
          type: 'toggle',
        },
      ],
      onSave: async (values) => {
        await editGroup({
          variables: {
            ...values,
          },
        });
      },
    });
    return () => {
      forms.unregister('addGroup');
      forms.unregister('editGroup');
    };
  }, [users, userGroups, forms, federation.id, addGroup, editGroup]);

  const columns = useMemo(() => {
    return [
      {
        key: 'name',
        valueGetter: (params) => {
          return params.data.name;
        },
        label: 'Group Name',
        width: 300,
      },
      {
        key: 'organisationCount',
        valueGetter: ({ data }) => data.users.length,
        label: 'Number of Users',
        width: 150,
      },
      {
        key: 'enabled',
        label: 'Enabled',
        valueGetter: (params) => params.data.enabled,
        width: 150,
      },
    ];
  }, []);

  return (
    <>
      <ConfirmDeleteAlert />
      <SettingsPanel
        title="User Groups"
        loading={loading}
        error={error}
        actions={[
          {
            text: 'Add Group',
            icon: 'user-plus',
            onClick: () => {
              forms.showForm('addGroup');
            },
          },
        ]}
        fixedHeight="750px"
      >
        <GridView
          sizeColumnsToFit
          showSideBar={false}
          contextMenuItems={(nodes) => {
            if (nodes.length !== 1) return null;
            return [
              {
                name: 'Delete Group',
                action: (groups) => {
                  showConfirmDeleteAlert({ id: groups[0].data.id });
                },
              },
            ];
          }}
          rows={userGroups}
          columns={columns}
          onRowDoubleClicked={(row) => {
            const { id, name, users, enabled, includeAll } = row.data;
            if (includeAll) {
              return;
            }
            forms.showForm('editGroup', {
              initialValues: {
                id,
                name,
                users: users.map((user) => user.id),
                enabled,
              },
            });
          }}
        />
      </SettingsPanel>
    </>
  );
};

export default UserGroups;
